import { useState } from "react";

/* ─────────────────────────────────────────
   SVG Speedometer Gauge — exact match
───────────────────────────────────────── */
function polarToXY(cx, cy, r, angleDeg) {
  const rad = (angleDeg - 90) * Math.PI / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}
function arcPath(cx, cy, r, startDeg, endDeg) {
  const s = polarToXY(cx, cy, r, startDeg);
  const e = polarToXY(cx, cy, r, endDeg);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} 1 ${e.x} ${e.y}`;
}
function Gauge({ value, max, label }) {
  const W = 150, H = 150, cx = 75, cy = 80;
  const R_OUTER = 66, R_TRACK = 57, R_INNER = 44;
  const START = -135, SWEEP = 270;
  const pct = Math.min(value / max, 1);
  const fillEnd = START + pct * SWEEP;
  const TICK_COUNT = 30;
  const ticks = Array.from({ length: TICK_COUNT + 1 }, (_, i) => {
    const angle = START + (i / TICK_COUNT) * SWEEP;
    const isMajor = i % 5 === 0;
    const r1 = R_OUTER - (isMajor ? 11 : 6);
    const r2 = R_OUTER - 1;
    const a = polarToXY(cx, cy, r1, angle);
    const b = polarToXY(cx, cy, r2, angle);
    return (
      <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y}
        stroke={isMajor ? "#475569" : "#2d3f57"} strokeWidth={isMajor ? 1.8 : 1} />
    );
  });
  const needleAngle = START + pct * SWEEP;
  const nTip  = polarToXY(cx, cy, R_TRACK - 3, needleAngle);
  const nB1   = polarToXY(cx, cy, 9, needleAngle - 90);
  const nB2   = polarToXY(cx, cy, 9, needleAngle + 90);
  const displayVal = Number.isInteger(value) ? value : value.toFixed(1);
  const gradId = `bg_${label.replace(/\W/g, "")}`;
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg">
        <defs>
          <radialGradient id={gradId} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#1e2d45" />
            <stop offset="100%" stopColor="#080d18" />
          </radialGradient>
        </defs>
        {/* outer chrome bezel */}
        <circle cx={cx} cy={cy} r={R_OUTER + 6} fill="#141f30" stroke="#2e4060" strokeWidth="2" />
        <circle cx={cx} cy={cy} r={R_OUTER + 4} fill="none" stroke="#1a2d45" strokeWidth="1" />
        <circle cx={cx} cy={cy} r={R_OUTER + 2} fill="none" stroke="#3a5070" strokeWidth="0.6" />
        {/* face */}
        <circle cx={cx} cy={cy} r={R_OUTER} fill={`url(#${gradId})`} />
        {/* track */}
        <path d={arcPath(cx, cy, R_TRACK, START, START + SWEEP)}
          fill="none" stroke="#162035" strokeWidth="8" strokeLinecap="round" />
        {/* value fill */}
        <path d={arcPath(cx, cy, R_TRACK, START, fillEnd)}
          fill="none" stroke="#38bdf8" strokeWidth="8" strokeLinecap="round" />
        {/* ticks */}
        {ticks}
        {/* inner dark bowl */}
        <circle cx={cx} cy={cy} r={R_INNER} fill="#060b14" stroke="#162030" strokeWidth="1.5" />
        <circle cx={cx} cy={cy} r={R_INNER - 4} fill="none" stroke="#0d1a28" strokeWidth="1" />
        {/* value */}
        <text x={cx} y={cy + 9} textAnchor="middle"
          fontSize="22" fontWeight="bold"
          fontFamily="'Rajdhani', 'Courier New', monospace"
          fill="#e2e8f0" letterSpacing="-0.5">
          {displayVal}
        </text>
        {/* needle */}
        <polygon
          points={`${nTip.x},${nTip.y} ${nB1.x},${nB1.y} ${nB2.x},${nB2.y}`}
          fill="#f97316" opacity="0.95" />
        {/* center cap */}
        <circle cx={cx} cy={cy} r={6} fill="#253550" stroke="#3a5070" strokeWidth="1.2" />
        <circle cx={cx} cy={cy} r={3} fill="#64748b" />
      </svg>
      <div style={{
        fontSize: 9, color: "#64748b", textTransform: "uppercase",
        letterSpacing: "1.1px", textAlign: "center", fontFamily: "monospace",
      }}>{label}</div>
    </div>
  );
}

/* ─────────────────────────────────────────
   DATA
───────────────────────────────────────── */
const ALARMS_LEFT = [
  "COMMON SHUTDOWN","EMERGENCY SHUTDOWN","SHUTDOWN FROM SAFETY SYS",
  "L/O LOW PRESS SHUTDOWN","HT CFW OUT HIGH T SHUTDOWN","CONTROL SOURCE FAIL",
  "GOVERNOR SOURCE FAIL","LO FLT DIFF .P.H","FO FLT DIFF .P.H",
  "LO SUMP TK L.I","LO SUMP TK L.H","F/O DRAIN BOX HIGH LEVEL",
  "PRE LO PRESS LOW","NO 1/2 G/E PSU COMMON ABN",
  "NO 1/2 G/E PSU BATT. CHARGER ALM","COMMON ABNORMAL ALARM",
].map(l => ({ label: l, ok: true }));

const SENSORS = [
  ["HT CFW IN TEMP","75","C"],["HT CFW OUT TEMP","70","C"],["HT CFW INLET PRESS","4.46","BAR"],
  ["LT CFW IN TEMP","28","C"],["LT CFW OUT TEMP","31","C"],["LT CFW INLET PRESS","2.90","BAR"],
  ["LO INLET TEMP","65","C"],["LO INLET PRESS","4.59","BAR"],["LO FILTER INLET PRESS","5.12","BAR"],
  ["FO INLET TEMP","31","C"],["FO INLET PRESS","7.17","BAR"],["FO FILTER INLET PRESS","7.16","BAR"],
  ["NON DRIVEN END TEMP","65","C"],["CHARGE AIR OUT TEMP","55","C"],
  ["CHARGE AIR INLET PRESS","1.82","BAR"],["STARTING AIR INLET PRESS","10.53","BAR"],
  ["T/C EXH GAS OUT","3.21","C"],["T/C EXH GAS A IN","447","C"],
  ["T/C EXH GAS B IN","447","C"],["T/C L.O PRESS","3.04","BAR"],
].map(([label,value,unit]) => ({ label, value, unit }));

const GAUGES = [
  { value: 959,  max: 1200, label: "POWER (KW)" },
  { value: 1388, max: 1800, label: "AMPERAGE (A)" },
  { value: 454,  max: 600,  label: "VOLTAGE (V)" },
  { value: 59.9, max: 80,   label: "FREQUENCY (Hz)" },
];

const EXH_COLS = ["NO.1","NO.2","NO.3","NO.4","NO.5","NO.6"];
const EXH_TEMP = [605,613,613,612,619,624];
const EXH_DEV  = [7,1,1,1,7,11];

const BOTTOM_ALARMS = [
  ["BOTH TACHO FAILURE TRIP","SAFETY TRIP CIRCUIT FAILURE","JET SYSTEM FAILURE","GOVERNOR FAIL"],
  ["T/C TACHO FAILURE","MONITORING SYSTEM FAILURE","TACHO FAILURE","OVERSPEED TRIP (1ST)"],
  ["OVERSPEED TRIP (2ND)","START FAILURE","STOP FAILURE","SAFETY SYSTEM FAILURE"],
  ["EXH GAS TEMP HIGH OR LOW","3.W PRE HEATER ON!","3.W PRE HEATER ON!"],
];

const LEGEND = [
  { color:"#3b82f6", label:"Main Engine" },
  { color:"#22c55e", label:"Auxiliary Engine" },
  { color:"#eab308", label:"Auxiliary Boiler" },
  { color:"#94a3b8", label:"Power Management System" },
];

/* ─────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────── */
export default function EngineMonitor() {
  const [activeGen, setActiveGen] = useState("1");
  const [activeTab, setActiveTab] = useState("G/E COMMON");
  const [sections, setSections] = useState({ operational:true, performance:true, service:true, fault:true });

  return (
    <div className="page-content">
      <div className="container-fluid" style={{ padding: 0 }}>
        <div style={s.root}>

          {/* TOP TABS */}
          <header style={s.nav}>
            <div style={s.tabGroup}>
              {["G/E COMMON","GENERATORS"].map(t => (
                <button key={t} onClick={() => setActiveTab(t)}
                  style={{ ...s.tabBtn, ...(activeTab===t ? s.tabActive : {}) }}>{t}</button>
              ))}
            </div>
            <div style={s.genGroup}>
              {["1","2","3","4"].map(n => (
                <button key={n} onClick={() => setActiveGen(n)}
                  style={{ ...s.genBtn, ...(activeGen===n ? s.genActive : {}) }}>{n}</button>
              ))}
            </div>
          </header>

          {/* MAIN 3-COL GRID */}
          <div style={s.grid}>

            {/* COL 1 — Alarms */}
            <div style={s.alarmCol}>
              {ALARMS_LEFT.map((a,i) => (
                <div key={i} style={s.alarmRow}>
                  <span style={s.alarmLbl}>{a.label}</span>
                  <span style={{ ...s.dot, backgroundColor: a.ok ? "#22c55e" : "#ef4444" }} />
                </div>
              ))}
            </div>

            {/* COL 2 — Sensors */}
            <div style={s.sensorCol}>
              {SENSORS.map((r,i) => (
                <div key={i} style={s.sensorRow}>
                  <span style={s.sensorLbl}>{r.label}</span>
                  <div style={s.sensorBadge}>
                    <span style={s.sensorVal}>{r.value}</span>
                    <span style={s.sensorUnit}>{r.unit}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* COL 3 — Gauges + data + engine */}
            <div style={s.rightCol}>

              {/* GAUGES — dark strip */}
              <div style={s.gaugeStrip}>
                {GAUGES.map(g => <Gauge key={g.label} {...g} />)}
              </div>

              {/* MIDDLE — exhaust stats */}
              <div style={s.middleStrip}>
                <div style={s.stripStats}>
                  {[
                    ["EXH. GAS MEAN","412 °C"],
                    ["ENGINE SPEED","899 RPM"],
                    ["T/C RPM PICK UP","27760 RPM"],
                  ].map(([k,v]) => (
                    <div key={k} style={s.miniStat}>
                      <span style={s.miniKey}>{k}</span>
                      <span style={s.miniVal}>{v}</span>
                    </div>
                  ))}
                </div>
                <div style={s.exhWrap}>
                  <div style={s.exhHead}>
                    <span style={s.exhLblCell} />
                    {EXH_COLS.map(c => <span key={c} style={s.exhHCell}>{c}</span>)}
                  </div>
                  {[["EXH. GAS TEMP [°C]",EXH_TEMP],["EXH. GAS DEVIATION",EXH_DEV]].map(([label,vals]) => (
                    <div key={label} style={s.exhRow}>
                      <span style={s.exhLblCell}>{label}</span>
                      {vals.map((v,i) => <span key={i} style={s.exhCell}>{v}</span>)}
                    </div>
                  ))}
                </div>
              </div>

              {/* ENGINE VISUAL */}
              <div style={s.engineArea}>
                <div style={s.engineVisual}>
                  {/* Blue motor body */}
                  <div style={s.motorBody} />
                  {/* Cylinder block */}
                  <div style={s.cylBlock}>
                    {[...Array(6)].map((_,i) => (
                      <div key={i} style={s.cyl} />
                    ))}
                  </div>
                  {/* Winding temp overlay */}
                  <div style={s.windBox}>
                    <div style={s.windTitle}>WINDING TEMP [°C]</div>
                    {[["U1","55"],["V1","44"],["W1","28"]].map(([k,v]) => (
                      <div key={k} style={s.windRow}>
                        <span style={s.windKey}>{k}</span>
                        <span style={s.windVal}>{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Control buttons */}
                <div style={s.ctrlRow}>
                  {[
                    { label:"BLOCKING", style: s.ctrlDefault },
                    { label:"STOP",     style: s.ctrlStop },
                    { label:"REMOTE",   style: s.ctrlRemote },
                    { label:"T/C ENGAGED", style: s.ctrlEngaged },
                  ].map(b => (
                    <button key={b.label} style={{ ...s.ctrlBtn, ...b.style }}>{b.label}</button>
                  ))}
                </div>
              </div>

            </div>
          </div>

          {/* BOTTOM ALARM GRID */}
          <div style={s.bottomGrid}>
            {BOTTOM_ALARMS.map((col,ci) => (
              <div key={ci} style={s.botCol}>
                {col.map((lbl,ai) => (
                  <div key={ai} style={s.alarmRowSm}>
                    <span style={s.alarmLblSm}>{lbl}</span>
                    <span style={{ ...s.dot, backgroundColor:"#22c55e" }} />
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* LEGEND */}
          <div style={s.legend}>
            {LEGEND.map(l => (
              <div key={l.label} style={s.legendItem}>
                <span style={{ ...s.legendDot, backgroundColor: l.color }} />
                <span style={s.legendLbl}>{l.label}</span>
              </div>
            ))}
          </div>

        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   STYLES
───────────────────────────────────────── */
const s = {
  root: { display:"flex", flexDirection:"column", minHeight:"100vh", backgroundColor:"#f1f5f9", fontFamily:"'DM Sans','Segoe UI',sans-serif", color:"#1e293b", fontSize:12 },

  /* nav */
  nav: { display:"flex", alignItems:"center", gap:12, padding:"10px 14px", backgroundColor:"#ffffff", borderBottom:"1px solid #e2e8f0" },
  tabGroup: { display:"flex", borderRadius:7, overflow:"hidden", border:"1px solid #cbd5e1" },
  tabBtn: { padding:"6px 16px", fontSize:12, fontWeight:600, backgroundColor:"#f8fafc", color:"#64748b", border:"none", borderRight:"1px solid #cbd5e1", cursor:"pointer" },
  tabActive: { backgroundColor:"#1e293b", color:"#fff" },
  genGroup: { display:"flex", gap:6 },
  genBtn: { width:36, height:36, borderRadius:8, backgroundColor:"#f1f5f9", border:"1px solid #cbd5e1", color:"#475569", fontWeight:700, fontSize:14, cursor:"pointer" },
  genActive: { backgroundColor:"#0f172a", color:"#fff", borderColor:"#0f172a" },

  /* main grid */
  grid: { display:"grid", gridTemplateColumns:"196px 230px 1fr", flex:1, overflow:"hidden" },

  /* alarm col */
  alarmCol: { backgroundColor:"#ffffff", borderRight:"1px solid #e2e8f0", overflowY:"auto" },
  alarmRow: { display:"flex", alignItems:"center", justifyContent:"space-between", padding:"4px 10px", borderBottom:"1px solid #f1f5f9" },
  alarmLbl: { fontSize:11, color:"#334155", fontWeight:500, flex:1, paddingRight:6 },
  dot: { width:10, height:10, borderRadius:"50%", flexShrink:0 },

  /* sensor col */
  sensorCol: { backgroundColor:"#f8fafc", borderRight:"1px solid #e2e8f0", overflowY:"auto" },
  sensorRow: { display:"flex", alignItems:"center", justifyContent:"space-between", padding:"4px 9px", borderBottom:"1px solid #e2e8f0" },
  sensorLbl: { fontSize:10, color:"#475569", fontWeight:500, flex:1 },
  sensorBadge: { display:"flex", alignItems:"center", gap:4, backgroundColor:"#e2e8f0", borderRadius:5, padding:"2px 7px" },
  sensorVal: { fontSize:11, fontWeight:700, color:"#0f172a" },
  sensorUnit: { fontSize:9, color:"#64748b", fontWeight:600 },

  /* right col */
  rightCol: { display:"flex", flexDirection:"column", backgroundColor:"#f1f5f9" },

  /* gauge strip */
  gaugeStrip: { display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8, padding:"14px 16px", backgroundColor:"#0f172a" },

  /* middle strip */
  middleStrip: { display:"flex", gap:10, padding:"8px 14px", backgroundColor:"#1e293b", alignItems:"flex-start" },
  stripStats: { display:"flex", flexDirection:"column", gap:4, flexShrink:0 },
  miniStat: { display:"flex", gap:8, alignItems:"center", backgroundColor:"#0f172a", borderRadius:6, padding:"4px 10px", border:"1px solid #334155" },
  miniKey: { fontSize:9, color:"#64748b", textTransform:"uppercase", letterSpacing:"0.6px" },
  miniVal: { fontSize:11, fontWeight:700, color:"#e2e8f0" },
  exhWrap: { flex:1 },
  exhHead: { display:"flex", gap:2, marginBottom:3 },
  exhRow: { display:"flex", gap:2, marginBottom:2 },
  exhLblCell: { fontSize:9, color:"#94a3b8", width:130, flexShrink:0, display:"flex", alignItems:"center" },
  exhHCell: { flex:1, textAlign:"center", fontSize:9, fontWeight:700, color:"#94a3b8" },
  exhCell: { flex:1, textAlign:"center", fontSize:10, fontWeight:600, backgroundColor:"#0f172a", color:"#e2e8f0", border:"1px solid #334155", borderRadius:4, padding:"2px 0" },

  /* engine area */
  engineArea: { flex:1, display:"flex", flexDirection:"column", padding:"10px 14px", gap:10 },
  engineVisual: { flex:1, backgroundColor:"#dde3ec", borderRadius:10, border:"1px solid #cbd5e1", display:"flex", alignItems:"center", padding:"14px 16px", gap:14, position:"relative", overflow:"hidden", minHeight:110 },
  motorBody: { width:88, height:78, borderRadius:10, backgroundColor:"#3b82f6", boxShadow:"0 4px 16px rgba(59,130,246,0.35)", flexShrink:0 },
  cylBlock: { display:"flex", alignItems:"flex-end", gap:5, flex:1, height:70 },
  cyl: { flex:1, height:"100%", backgroundColor:"#94a3b8", borderRadius:"5px 5px 0 0", border:"1px solid #64748b" },
  windBox: { position:"absolute", right:14, top:"50%", transform:"translateY(-50%)", backgroundColor:"#1e293b", borderRadius:8, padding:"7px 12px", minWidth:120 },
  windTitle: { fontSize:9, color:"#94a3b8", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.6px" },
  windRow: { display:"flex", justifyContent:"space-between", gap:10, marginBottom:2 },
  windKey: { fontSize:11, color:"#64748b", fontWeight:600 },
  windVal: { fontSize:11, color:"#22d3ee", fontWeight:700 },
  ctrlRow: { display:"flex", gap:8 },
  ctrlBtn: { flex:1, padding:"8px 0", borderRadius:8, fontWeight:700, fontSize:11, cursor:"pointer", textTransform:"uppercase", letterSpacing:"0.5px", border:"1px solid" },
  ctrlDefault: { backgroundColor:"#e2e8f0", borderColor:"#cbd5e1", color:"#475569" },
  ctrlStop: { backgroundColor:"#fee2e2", borderColor:"#fca5a5", color:"#dc2626" },
  ctrlRemote: { backgroundColor:"#dcfce7", borderColor:"#86efac", color:"#16a34a" },
  ctrlEngaged: { backgroundColor:"#dbeafe", borderColor:"#93c5fd", color:"#2563eb" },

  /* bottom alarms */
  bottomGrid: { display:"grid", gridTemplateColumns:"repeat(4,1fr)", backgroundColor:"#ffffff", borderTop:"1px solid #e2e8f0" },
  botCol: { borderRight:"1px solid #e2e8f0", padding:"4px" },
  alarmRowSm: { display:"flex", alignItems:"center", justifyContent:"space-between", padding:"3px 8px", borderBottom:"1px solid #f1f5f9" },
  alarmLblSm: { fontSize:10, color:"#334155", fontWeight:500, flex:1, paddingRight:4 },

  /* legend */
  legend: { display:"flex", gap:24, alignItems:"center", padding:"10px 16px", backgroundColor:"#ffffff", borderTop:"1px solid #e2e8f0" },
  legendItem: { display:"flex", alignItems:"center", gap:8 },
  legendDot: { width:26, height:26, borderRadius:6, flexShrink:0 },
  legendLbl: { fontSize:12, color:"#475569", fontWeight:500 },
};
